import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagContext';

// Generated at build/startup by scripts/scan-feature-flag-keys.js
import pageFlagMap from '../../config/pageFlagMap.json';

function splitPath(pathname) {
  return String(pathname || '/')
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean);
}

function matchesPattern(pathname, pattern) {
  const a = splitPath(pathname);
  const b = splitPath(pattern);
  if (pattern === '/' && (pathname === '/' || splitPath(pathname).length === 0)) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const seg = b[i];
    if (seg.startsWith(':')) continue;
    if (seg !== a[i]) return false;
  }
  return true;
}

function resolveFlagKey(pathname, mapObj) {
  if (!mapObj || typeof mapObj !== 'object') return null;
  const patterns = Object.keys(mapObj).sort((x, y) => y.length - x.length);
  for (const pattern of patterns) {
    if (matchesPattern(pathname, pattern)) return mapObj[pattern];
  }
  return null;
}

/** Scan-generated page flags → canonical HR hub flags in defaultFlags. */
function isHrChildPageEnabled(
  flagKey,
  { hrAttendance, hrSurveys: _hrSurveys, hrPerformance, hrSuggestionBox, hrSes }
) {
  if (
    flagKey === 'ui.pages.AttendanceRegisters' ||
    flagKey === 'ui.pages.AttendanceRegisterCreate' ||
    flagKey === 'ui.pages.AttendanceRegisterDetail'
  ) {
    return hrAttendance;
  }
  if (flagKey === 'ui.pages.SuggestionBoxAdmin') {
    return hrSuggestionBox;
  }
  if (
    flagKey.startsWith('ui.pages.PerformanceReview') ||
    flagKey.startsWith('ui.pages.ReviewEmail')
  ) {
    return hrPerformance;
  }
  if (flagKey.startsWith('ui.pages.Ses')) {
    return hrSes;
  }
  return null;
}

/**
 * Detail routes historically scanned as ui.pages.*Detail, which are not in nav FeatureGates
 * or defaultFlags. Map them to the list-page flags so PageGuard matches RBAC users already have.
 */
const PAGE_FLAG_ALIASES = {
  'ui.pages.MemberDetail': 'ui.pages.Members',
  'ui.pages.RepositoryDetail': 'ui.pages.Repositories',
  'ui.pages.ProjectDetail': 'ui.pages.Projects',
};

function resolveEffectiveFlagKey(flagKey) {
  if (!flagKey) return null;
  return PAGE_FLAG_ALIASES[flagKey] ?? flagKey;
}

export default function PageGuard({ children }: any) {
  const { pathname } = useLocation();
  const { loading } = useFeatureFlags();

  const flagKey = useMemo(() => {
    const raw = resolveFlagKey(pathname, pageFlagMap);
    return resolveEffectiveFlagKey(raw);
  }, [pathname]);
  const mapFlagEnabled = useFeatureFlag(flagKey ?? '__page_guard__', false);
  const portfoliosNew = useFeatureFlag('ui.pages.Portfolios', false);
  const portfoliosLegacy = useFeatureFlag('ui.pages.Library', false);
  const hrAttendance = useFeatureFlag('ui.pages.AttendanceRegister', false);
  const hrSurveys = useFeatureFlag('ui.pages.Surveys', false);
  const hrPerformance = useFeatureFlag('ui.pages.PerformanceReviews', false);
  const hrSuggestionBox = useFeatureFlag('ui.pages.SuggestionBox', false);
  const hrSes = useFeatureFlag('ui.pages.StreamEmpowermentSessions', false);

  // If we don't have a mapping, don't block render (covers unknown routes / NotFound).
  if (!flagKey) return <>{children}</>;

  // Don't redirect while flags are still loading (e.g. on refresh), or we'd always send users to dashboard.
  if (loading) return <>{children}</>;

  const enabled =
    flagKey === 'ui.pages.Portfolios'
      ? portfoliosNew || portfoliosLegacy
      : flagKey === 'ui.pages.HR'
        ? hrAttendance || hrSurveys || hrPerformance || hrSuggestionBox
        : (isHrChildPageEnabled(flagKey, {
            hrAttendance,
            hrSurveys,
            hrPerformance,
            hrSuggestionBox,
            hrSes,
          }) ?? mapFlagEnabled);

  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}
